import { useState } from 'react';

import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { z } from 'zod';

import {
  adminApi,
  type AdminMember,
  type AdminTenant,
  type AdminTenantStats,
  type AdminTenantView,
} from '@/api/queries/admin';
import { isSuperAdmin, useTenantStore } from '@/auth/tenantStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import { useModal } from '@/components/modal/useModal';
import { INVITE_ROLE_HINT } from '@/components/team/inviteRoleHint';
import { MembersTable } from '@/components/team/MembersTable';
import { ExternalLinksEditor } from '@/components/tenant/ExternalLinksEditor';
import {
  Button,
  Field,
  Icon,
  Input,
  Modal,
  StatCard,
  StatusPill,
  useToast,
  type StatusTone,
} from '@/components/ui';
import { DEFAULT_BRAND } from '@/utils/brandPalette';
import { formatDate, formatDateTime } from '@/utils/formatDate';

type TabValue = 'overview' | 'members' | 'views' | 'branding' | 'links';

const searchSchema = z.object({
  tab: z.enum(['overview', 'members', 'views', 'branding', 'links']).catch('overview'),
});

const TABS: { value: TabValue; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'members', label: 'Members' },
  { value: 'views', label: 'Views' },
  { value: 'branding', label: 'Branding' },
  { value: 'links', label: 'External links' },
];

function tenantStatusTone(status: string): StatusTone {
  if (status === 'active') return 'active';
  if (status === 'archived') return 'dnc';
  return 'inactive';
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function TenantDetailRoute() {
  const { tenantId } = Route.useParams();
  const { tab } = useSearch({ from: '/admin/tenants/$tenantId/' });
  const navigate = useNavigate();
  const { data: tenant, isPending, isError } = adminApi.useTenant(tenantId);

  useSetBreadcrumbs([
    { label: 'Admin' },
    { label: 'Tenants', to: '/admin/tenants' },
    { label: tenant?.name ?? 'Tenant' },
  ]);

  if (isPending) return <LoadingSpinner message="Loading tenant..." />;
  if (isError || !tenant) {
    return (
      <div style={{ padding: 24, color: 'var(--text-2)' }}>
        Tenant not found. <a href="/admin/tenants">Back to tenants</a>
      </div>
    );
  }

  const setTab = (next: TabValue) =>
    navigate({
      to: '/admin/tenants/$tenantId',
      params: { tenantId },
      search: { tab: next },
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DetailHeader tenant={tenant} />
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {TABS.map((t) => {
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                color: active ? 'var(--text-1)' : 'var(--text-3)',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' ? (
        <OverviewTab tenantId={tenantId} />
      ) : tab === 'branding' ? (
        <BrandingTab tenant={tenant} />
      ) : tab === 'links' ? (
        <ExternalLinksTab tenant={tenant} />
      ) : tab === 'views' ? (
        <ViewsTab tenantId={tenantId} />
      ) : (
        <MembersTab tenantId={tenantId} />
      )}
    </div>
  );
}

function DetailHeader({ tenant }: { tenant: AdminTenant }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { openModal } = useModal();
  const rename = adminApi.useRenameTenant();
  const archive = adminApi.useArchiveTenant();
  const del = adminApi.useDeleteTenant();
  const [renameOpen, setRenameOpen] = useState(false);

  const handleArchive = () => {
    openModal(
      <ConfirmDialog
        title="Archive tenant"
        message={`Archive "${tenant.name}"? This is reversible only by a developer (no UI to un-archive yet).`}
        confirmLabel="Archive"
        onConfirm={async () => {
          try {
            await archive.mutateAsync(tenant.id);
            toast.show({ title: `Tenant "${tenant.name}" archived`, severity: 'success' });
            navigate({ to: '/admin/tenants' });
          } catch (err) {
            toast.show({
              title: 'Failed to archive tenant',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  const handleDelete = () => {
    openModal(
      <ConfirmDialog
        title="Delete tenant permanently"
        message={`Permanently delete "${tenant.name}"? This cannot be undone — all of its data (contacts, members, tags, segments, views, integrations and audit history) will be erased.`}
        confirmLabel="Delete permanently"
        destructive
        onConfirm={async () => {
          try {
            await del.mutateAsync(tenant.id);
            toast.show({ title: `Tenant "${tenant.name}" deleted`, severity: 'success' });
            navigate({ to: '/admin/tenants' });
          } catch (err) {
            toast.show({
              title: 'Failed to delete tenant',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  const isArchived = tenant.status === 'archived';

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{tenant.name}</h1>
          <StatusPill status={tenantStatusTone(tenant.status)}>{statusLabel(tenant.status)}</StatusPill>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
          <code>{tenant.slug}</code> · created {formatDate(tenant.createdAt) ?? '—'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="secondary"
          leftIcon={<Icon name="edit" size={14} />}
          onClick={() => setRenameOpen(true)}
          disabled={isArchived}
        >
          Rename
        </Button>
        <Button
          variant="danger"
          leftIcon={<Icon name="trash" size={14} />}
          onClick={handleArchive}
          disabled={isArchived}
        >
          Archive
        </Button>
        {isArchived && (
          <Button
            variant="danger"
            leftIcon={<Icon name="trash" size={14} />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        )}
      </div>

      {renameOpen && (
        <RenameTenantModal
          tenant={tenant}
          onClose={() => setRenameOpen(false)}
          onSubmit={async (name) => {
            await rename.mutateAsync({ id: tenant.id, name });
            toast.show({ title: 'Tenant renamed', severity: 'success' });
          }}
          loading={rename.isPending}
        />
      )}
    </div>
  );
}

function RenameTenantModal({
  tenant,
  onClose,
  onSubmit,
  loading,
}: {
  tenant: AdminTenant;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  loading: boolean;
}) {
  const [name, setName] = useState(tenant.name);
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === tenant.name) return;
    await onSubmit(trimmed);
    onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename tenant"
      size="sm"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={loading}
            disabled={!name.trim() || name.trim() === tenant.name}
          >
            Save
          </Button>
        </>
      }
    >
      <Field label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

function OverviewTab({ tenantId }: { tenantId: string }) {
  const { data: stats, isPending } = adminApi.useTenantStats(tenantId);
  if (isPending) return <LoadingSpinner message="Loading stats..." />;
  if (!stats) return <p style={{ color: 'var(--text-3)' }}>No stats available.</p>;
  return <StatsGrid stats={stats} />;
}

function StatsGrid({ stats }: { stats: AdminTenantStats }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
      <StatCard label="Contacts" value={stats.contacts} tone="brand" icon="users" />
      <StatCard label="Active members" value={stats.activeMembers} tone="info" icon="users" />
      <StatCard label="Pending invites" value={stats.pendingInvites} tone="amber" icon="mail" />
      <StatCard label="Tags" value={stats.tags} icon="tags" />
      <StatCard label="Segments" value={stats.segments} icon="segments" />
      <StatCard label="Consents" value={stats.consents} icon="shield" />
      <StatCard label="Audit events (30d)" value={stats.auditEvents30d} icon="history" />
      <StatCard
        label="Last activity"
        value={formatDateTime(stats.lastActivityAt) ?? 'Never'}
        icon="clock"
      />
    </div>
  );
}

function BrandingTab({ tenant }: { tenant: AdminTenant }) {
  const toast = useToast();
  const updateConfig = adminApi.useUpdateTenantConfig();
  const [accentColor, setAccentColor] = useState(tenant.accentColor ?? DEFAULT_BRAND);
  const [logoInitials, setLogoInitials] = useState(tenant.logoInitials ?? '');

  const trimmedColor = accentColor.trim();
  const trimmedInitials = logoInitials.trim();
  const hexValid = /^#[0-9a-fA-F]{6}$/.test(trimmedColor);
  const initialsValid = trimmedInitials.length >= 1 && trimmedInitials.length <= 3;
  const dirty =
    trimmedColor.toLowerCase() !== (tenant.accentColor ?? DEFAULT_BRAND).toLowerCase() ||
    trimmedInitials.toUpperCase() !== (tenant.logoInitials ?? '').toUpperCase();

  const previewBadge = trimmedInitials.toUpperCase() || tenant.name.slice(0, 2).toUpperCase();

  const save = async () => {
    if (!hexValid || !initialsValid) return;
    try {
      await updateConfig.mutateAsync({
        id: tenant.id,
        accentColor: trimmedColor.toLowerCase(),
        logoInitials: trimmedInitials.toUpperCase(),
      });
      toast.show({ title: 'Branding updated', severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to update branding',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
        Controls the tenant&apos;s sidebar badge and the accent color across the app for its members.
      </div>

      {/* Live preview of the sidebar brand block */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 12,
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface-2)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: hexValid ? trimmedColor : 'var(--text-4)',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {previewBadge}
        </div>
        <div>
          <div style={{ fontWeight: 600 }}>{tenant.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Datahub</div>
        </div>
      </div>

      <Field label="Logo initials" required>
        <Input
          value={logoInitials}
          maxLength={3}
          placeholder="MI"
          error={!!logoInitials && !initialsValid}
          onChange={(e) => setLogoInitials(e.target.value)}
          style={{ maxWidth: 120 }}
        />
      </Field>

      <Field label="Accent color" required>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="color"
            aria-label="Accent color picker"
            value={hexValid ? trimmedColor : DEFAULT_BRAND}
            onChange={(e) => setAccentColor(e.target.value)}
            style={{ width: 40, height: 34, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
          />
          <Input
            value={accentColor}
            placeholder={DEFAULT_BRAND}
            error={!hexValid}
            onChange={(e) => setAccentColor(e.target.value)}
            style={{ maxWidth: 140, fontFamily: 'var(--font-mono)' }}
          />
        </div>
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          onClick={save}
          loading={updateConfig.isPending}
          disabled={!dirty || !hexValid || !initialsValid}
        >
          Save branding
        </Button>
      </div>
    </div>
  );
}

function ExternalLinksTab({ tenant }: { tenant: AdminTenant }) {
  const update = adminApi.useUpdateTenantExternalLinks();
  return (
    <ExternalLinksEditor
      links={tenant.externalLinks ?? []}
      saving={update.isPending}
      onSave={(externalLinks) => update.mutateAsync({ id: tenant.id, externalLinks })}
    />
  );
}

function MembersTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const { openModal } = useModal();
  const { data: members, isPending } = adminApi.useTenantMembers(tenantId);
  const deactivate = adminApi.useDeactivateMember();
  const remove = adminApi.useRemoveMember();
  const [inviteOpen, setInviteOpen] = useState(false);

  const onDeactivate = (m: AdminMember) => {
    openModal(
      <ConfirmDialog
        title="Deactivate member"
        message={`Deactivate ${m.invitedEmail ?? 'this member'}? They will lose access to this tenant.`}
        confirmLabel="Deactivate"
        onConfirm={async () => {
          try {
            await deactivate.mutateAsync({ tenantId, membershipId: m.id });
            toast.show({ title: 'Member deactivated', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to deactivate',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  const onRemove = (m: AdminMember) => {
    openModal(
      <ConfirmDialog
        title="Remove member"
        message={`Remove ${m.invitedEmail ?? 'this member'}? Their membership row will be deleted.`}
        confirmLabel="Remove"
        onConfirm={async () => {
          try {
            await remove.mutateAsync({ tenantId, membershipId: m.id });
            toast.show({ title: 'Member removed', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to remove',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          leftIcon={<Icon name="plus" size={14} />}
          onClick={() => setInviteOpen(true)}
        >
          Invite member
        </Button>
      </div>
      <MembersTable
        members={members ?? []}
        loading={isPending}
        onDeactivate={onDeactivate}
        onRemove={onRemove}
      />
      {inviteOpen && <InviteMemberModal tenantId={tenantId} onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function InviteMemberModal({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const invite = adminApi.useInviteMember();
  const toast = useToast();
  const [email, setEmail] = useState('');

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await invite.mutateAsync({ tenantId, email: trimmed });
      toast.show({ title: `Invited ${trimmed}`, severity: 'success' });
      onClose();
    } catch (err) {
      const conflict = isAxiosError(err) && err.response?.status === 409;
      toast.show({
        title: conflict
          ? `${trimmed} is already an active member of this tenant.`
          : 'Failed to invite',
        description: conflict ? undefined : err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite member"
      size="sm"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={invite.isPending}
            disabled={!email.trim()}
          >
            Invite
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Email" required hint={INVITE_ROLE_HINT}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="someone@example.com"
            autoFocus
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Views tab

const VIEW_COLORS = ['#1F47AB', '#0E8E8E', '#0F9D6B', '#D97706', '#C0392B', '#7C3AED', '#475569'];

// Starter templates the admin can one-click add. `q` is the compact contacts
// filter query (parseQuery.ts); empty `q` means "all contacts".
const VIEW_PRESETS: { label: string; q: string; color: string }[] = [
  { label: 'All contacts', q: '', color: '#475569' },
  { label: 'Lékaři', q: 'tags:kategorie:lekar', color: '#1F47AB' },
  { label: 'Studenti medicíny', q: 'tags:kategorie:student', color: '#0E8E8E' },
  { label: 'VIP', q: 'tags:vip', color: '#D97706' },
  { label: 'Workshops 2026', q: 'tags:workshopy', color: '#0F9D6B' },
  { label: 'Do not contact', q: 'status.doNotContact=true', color: '#C0392B' },
];

function viewQuery(v: AdminTenantView): string {
  const q = (v.filters as { q?: unknown })?.q;
  return typeof q === 'string' ? q : '';
}

function ViewsTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const { openModal } = useModal();
  const { data: views = [], isPending } = adminApi.useTenantViews(tenantId);
  const createView = adminApi.useCreateTenantView();
  const deleteView = adminApi.useDeleteTenantView();
  const [addOpen, setAddOpen] = useState(false);

  const existingNames = new Set(views.map((v) => v.name.toLowerCase()));

  const addPreset = async (preset: { label: string; q: string; color: string }) => {
    if (existingNames.has(preset.label.toLowerCase())) {
      toast.show({ title: `"${preset.label}" already exists`, severity: 'error' });
      return;
    }
    try {
      await createView.mutateAsync({
        tenantId,
        name: preset.label,
        color: preset.color,
        filters: preset.q ? { q: preset.q } : {},
      });
      toast.show({ title: `Added "${preset.label}"`, severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to add view',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  const onDelete = (v: AdminTenantView) => {
    openModal(
      <ConfirmDialog
        title="Delete global view"
        message={`Delete the shared view "${v.name}" for this tenant? Members will no longer see this tab.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await deleteView.mutateAsync({ tenantId, id: v.id });
            toast.show({ title: 'View deleted', severity: 'success' });
          } catch (err) {
            toast.show({
              title: 'Failed to delete',
              description: err instanceof Error ? err.message : undefined,
              severity: 'error',
            });
          }
        }}
      />,
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-3)', maxWidth: 560, lineHeight: 1.5 }}>
          Global views are shared filter tabs shown to everyone on this tenant’s Contacts list.
          By default a tenant has none — add your own below, or one-click a starter template.
        </p>
        <Button variant="primary" leftIcon={<Icon name="plus" size={14} />} onClick={() => setAddOpen(true)}>
          New view
        </Button>
      </div>

      {/* Quick-add starter templates */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>Starter templates</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {VIEW_PRESETS.map((p) => {
            const added = existingNames.has(p.label.toLowerCase());
            return (
              <button
                key={p.label}
                type="button"
                className="chip-btn"
                disabled={added || createView.isPending}
                onClick={() => void addPreset(p)}
                title={added ? 'Already added' : `Add "${p.label}"`}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
                {p.label}
                {added ? <Icon name="check" size={11} /> : <Icon name="plus" size={11} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Configured views */}
      {isPending ? (
        <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading views…</div>
      ) : views.length === 0 ? (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: '24px 16px',
            textAlign: 'center',
            color: 'var(--text-3)',
            fontSize: 13,
          }}
        >
          No global views yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {views.map((v) => (
            <div
              key={v.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: v.color ?? '#94A3B8' }} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{v.name}</span>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-3)' }}>
                {viewQuery(v) || '(all contacts)'}
              </span>
              <Button variant="ghost" size="sm" onClick={() => onDelete(v)}>
                <Icon name="trash" size={13} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {addOpen && <AddViewModal tenantId={tenantId} onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AddViewModal({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const create = adminApi.useCreateTenantView();
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState(VIEW_COLORS[0]);
  const [q, setQ] = useState('');

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({
        tenantId,
        name: trimmed,
        color,
        filters: q.trim() ? { q: q.trim() } : {},
      });
      toast.show({ title: `Added "${trimmed}"`, severity: 'success' });
      onClose();
    } catch (err) {
      toast.show({
        title: 'Failed to add view',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New global view"
      size="sm"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending} disabled={!name.trim()}>
            Add view
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VIP, Lékaři, …"
            autoFocus
          />
        </Field>
        <Field label="Color">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {VIEW_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setColor(c)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: c,
                  border: c === color ? '2px solid var(--text-1)' : '2px solid transparent',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </Field>
        <Field label="Filter query" hint="Compact contacts filter, e.g. tags:vip or status.doNotContact=true. Leave empty for all contacts.">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="tags:vip"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </Field>
      </div>
    </Modal>
  );
}

export const Route = createFileRoute('/admin/tenants/$tenantId/')({
  validateSearch: searchSchema,
  beforeLoad: () => {
    if (!isSuperAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: TenantDetailRoute,
  errorComponent: ErrorComponent,
});
